import { useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Flame, TrendingUp, Percent, Clock, Disc, Disc3, ArrowRight, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HotProductBadge } from "./HotProductBadge";

interface Product {
  id: string;
  name: string;
  artist?: string;
  price: number;
  originalPrice?: number;
  image: string;
  badge?: string;
  variants?: string[];
  colors?: string[];
  formats?: { type: "LP" | "CD" | "MC" | "DVD"; image?: string }[];
  releaseDate?: string;
  isHot?: boolean;
  isAdultOnly?: boolean;
}

interface Tab {
  id: string;
  label: string;
  products: Product[];
}

interface ProductTabsProps {
  title: string;
  tabs: Tab[];
  currency?: string;
}

const tabIcons: Record<string, React.ReactNode> = {
  novinky: <Flame className="w-5 h-5" />,
  najpredavanejsie: <TrendingUp className="w-5 h-5" />,
  zlava: <Percent className="w-5 h-5" />,
};

const formatIcons: Record<string, React.ReactNode> = {
  LP: <Disc3 className="w-3.5 h-3.5" />,
  CD: <Disc className="w-3.5 h-3.5" />,
};

export const ProductTabs = ({ title, tabs, currency = "€" }: ProductTabsProps) => {
  const [activeTab, setActiveTab] = useState(tabs[0]?.id || "");
  const productsPerRow = 5;
  const maxRows = 2;
  const maxProducts = productsPerRow * maxRows;

  const activeProducts = tabs.find(t => t.id === activeTab)?.products || [];
  const displayedProducts = activeProducts.slice(0, maxProducts);
  const hasMore = activeProducts.length > maxProducts;

  return (
    <section className="max-w-[1600px] mx-auto px-3 lg:px-6 py-8 lg:py-10">
      {/* Header - Mosaic style */}
      <div className="text-center mb-6 lg:mb-8">
        <p className="text-primary text-xs lg:text-sm font-bold uppercase tracking-widest mb-3 lg:mb-4">
          NÁVRHY PRE VÁS
        </p>
        <h2 className="text-2xl lg:text-4xl font-bold text-foreground mb-3 lg:mb-4">
          Objavte <span className="bg-gradient-to-r from-primary via-[hsl(170,70%,35%)] to-[hsl(170,60%,45%)] bg-clip-text text-transparent">náš výber</span>
        </h2>
        <p className="text-muted-foreground text-xs lg:text-lg max-w-2xl mx-auto mb-4 lg:mb-6 px-4">
          Nájdite to, čo práve hľadáte. Od noviniek cez bestsellery až po akciové ponuky.
        </p>
        
        {/* Tabs - scrollable on mobile, large on desktop */}
        <div className="overflow-x-auto scrollbar-hide -mx-3 px-3 lg:mx-0 lg:px-0">
          <div className="flex justify-start lg:justify-center">
            <div className="inline-flex items-center gap-1 lg:gap-2 bg-secondary p-1 lg:p-2 rounded-xl lg:rounded-2xl shrink-0">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center gap-1 lg:gap-2 px-3 lg:px-6 py-2 lg:py-3 rounded-lg lg:rounded-xl text-xs lg:text-base font-bold transition-all whitespace-nowrap shrink-0",
                    activeTab === tab.id
                      ? "bg-foreground text-background shadow-lg"
                      : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                  )}
                >
                  {tabIcons[tab.id]}
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid - 2 rows max */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-5">
        {displayedProducts.map((product, index) => (
          <Link
            key={`${product.id}-${index}`}
            to={product.isAdultOnly ? `/18-plus/${product.id}` : `/produkt/${product.id}`}
            className="group relative"
          >
            {/* HOT Badge */}
            {product.isHot && !product.isAdultOnly && <HotProductBadge />}
            
            {/* Image */}
            <div className="relative aspect-square rounded-xl overflow-hidden bg-secondary mb-3">
              <img
                src={product.image}
                alt={product.name}
                className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
                  product.isAdultOnly ? "blur-md" : ""
                }`}
              />
              
              {/* 18+ Badge */}
              {product.isAdultOnly && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-destructive text-destructive-foreground px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-lg">
                    <ShieldAlert className="w-4 h-4" />
                    <span className="font-bold text-sm">18+</span>
                  </div>
                </div>
              )}
              
              {/* Badge */}
              {product.badge && !product.isHot && !product.isAdultOnly && (
                <div className="absolute top-2 left-2">
                  <span className={cn(
                    "px-2 py-1 rounded text-[10px] font-bold text-white",
                    product.badge.includes("Limited") ? "bg-amber-500" :
                    product.badge.includes("Special") ? "bg-primary" :
                    "bg-foreground"
                  )}>
                    {product.badge.split(",")[0]}
                  </span>
                </div>
              )}

              {/* Discount */}
              {product.originalPrice && !product.isAdultOnly && (
                <div className="absolute top-2 right-2">
                  <span className="px-2 py-1 rounded text-[10px] font-bold bg-destructive text-white">
                    -{Math.round((1 - product.price / product.originalPrice) * 100)}%
                  </span>
                </div>
              )}

              {/* Release Date */}
              {product.releaseDate && !product.isAdultOnly && (
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="flex items-center gap-1 px-2 py-1 rounded bg-foreground/80 text-background text-[10px] font-medium">
                    <Clock className="w-3 h-3" />
                    {product.releaseDate}
                  </div>
                </div>
              )}
            </div>

            {/* Artist */}
            {product.artist && (
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                {product.artist}
              </p>
            )}

            {/* Name */}
            <h3 className="text-sm font-medium text-foreground mb-1 line-clamp-2 group-hover:text-primary transition-colors">
              {product.name}
            </h3>

            {/* Price */}
            <div className="flex items-center gap-2 mb-2">
              <span className="font-bold text-foreground">
                {product.price.toFixed(2).replace(".", ",")} {currency}
              </span>
              {product.originalPrice && (
                <span className="text-sm text-muted-foreground line-through">
                  {product.originalPrice.toFixed(2).replace(".", ",")} {currency}
                </span>
              )}
            </div>

            {/* Format Variants (LP/CD) */}
            {product.formats && product.formats.length > 0 && (
              <div className="flex gap-1">
                {product.formats.map((format, i) => (
                  <div
                    key={i}
                    className={cn(
                      "flex items-center gap-1 px-2 py-1 rounded border text-[10px] font-bold",
                      i === 0 ? "border-foreground bg-foreground/5" : "border-border"
                    )}
                  >
                    {formatIcons[format.type]}
                    {format.type}
                  </div>
                ))}
              </div>
            )}

            {/* Size Variants */}
            {product.variants && !product.formats && (
              <div className="flex gap-1 flex-wrap">
                {product.variants.slice(0, 5).map((v) => (
                  <span key={v} className="px-1.5 py-0.5 rounded border border-border text-[10px] text-muted-foreground">
                    {v}
                  </span>
                ))}
              </div>
            )}

            {/* Colors */}
            {product.colors && (
              <div className="flex gap-1 mt-1">
                {product.colors.map((c) => (
                  <span key={c} className="w-4 h-4 rounded-full border border-border" style={{ backgroundColor: c }} />
                ))}
              </div>
            )}
          </Link>
        ))}
      </div>

      {/* View All Button - Always show below products */}
      <div className="text-center mt-8">
        <Link to="/produkty">
          <Button 
            size="lg" 
            className="gap-3 font-bold px-8 py-6 text-base transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-primary/20 group"
          >
            Zobraziť viac
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-2" />
          </Button>
        </Link>
      </div>
    </section>
  );
};
